import React, { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

/**
 * fields: [{ key, label, type: text|number|select|date|textarea, options?, required?, default?, colSpan? }]
 */
export default function EntityFormDialog({ open, onClose, title, path, fields, transform, testid = "entity-form" }) {
  const qc = useQueryClient();
  const [f, setF] = useState({});

  useEffect(() => {
    if (open) {
      const init = {};
      fields.forEach((fl) => { init[fl.key] = fl.default ?? ""; });
      setF(init);
    }
  }, [open]); // eslint-disable-line

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    for (const fl of fields) {
      if (fl.required && (f[fl.key] === "" || f[fl.key] == null)) return toast.error(`${fl.label} is required`);
    }
    let payload = { ...f };
    fields.forEach((fl) => { if (fl.type === "number" && payload[fl.key] !== "") payload[fl.key] = Number(payload[fl.key]); });
    if (transform) payload = transform(payload);
    try {
      await api.post(`/${path}`, payload);
      toast.success(`${title} saved`);
      qc.invalidateQueries();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto erp-scroll" data-testid={testid}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          {fields.map((fl) => (
            <div key={fl.key} className={`space-y-1 ${fl.colSpan === 2 ? "col-span-2" : ""}`}>
              <Label className="text-xs">{fl.label}{fl.required && " *"}</Label>
              {fl.type === "select" ? (
                <Select value={String(f[fl.key] ?? "")} onValueChange={(v) => set(fl.key, v)}>
                  <SelectTrigger data-testid={`${testid}-${fl.key}`}><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{fl.options.map((o) => <SelectItem key={o} value={String(o)}>{o}</SelectItem>)}</SelectContent>
                </Select>
              ) : fl.type === "textarea" ? (
                <Textarea data-testid={`${testid}-${fl.key}`} value={f[fl.key] ?? ""} onChange={(e) => set(fl.key, e.target.value)} rows={2} />
              ) : (
                <Input data-testid={`${testid}-${fl.key}`} type={fl.type === "number" ? "number" : fl.type === "date" ? "date" : "text"}
                  value={f[fl.key] ?? ""} onChange={(e) => set(fl.key, e.target.value)} />
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid={`${testid}-save`} className="bg-orange-600 hover:bg-orange-700 text-white" onClick={save}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
